/**
 * Dev-only bridge: the webpack server queues commands, this poller runs them inside
 * the Word task pane (clicks + Word.run). Used by `npm run mcc:agent`.
 */

import { log } from '../utils/logging';
import { normalizeWordParagraphBaseline } from '../utils/bidiText';
import {
  cardActionSignals,
  cardLooksSubmitted,
  findAiSourceBocButton,
  findClauseCard,
  findQuickResponseButton,
  findQuickResponseOption,
  findSubmitButton,
  findUndoButton,
  foldLabel,
  isSubmitToResponseLabel,
  isUnusableQrCard,
  previewLooksLikeAiSuggestion,
} from './mccAgentCardDom';
import {
  evaluateSeeProposedApplyFromSnapshot,
  evaluateSeeProposedPreview,
  foldQrCheckText,
} from './mccAgentQuickResponseCheck';

/* global Word */

const PREFIX = '[MccAgent]'; // accepted-ooxml-v1 poller

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function foldWs(text: string): string {
  return normalizeWordParagraphBaseline(text || '').replace(/\s+/g, ' ').trim();
}

function findCard(clauseNumber: string): HTMLElement | null {
  return findClauseCard(document, clauseNumber);
}

function requireCard(clauseNumber: string): HTMLElement {
  const card = findCard(clauseNumber);
  if (card) return card;
  const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
  const ids = cards.map((c) => c.getAttribute('data-clause-id') || '');
  throw new Error(
    `clause card ${clauseNumber} not found (${cards.length} cards); idsMatching=${ids.filter((id) => id.includes(clauseNumber)).join(',') || 'none'}`,
  );
}

async function waitFor<T>(
  pred: () => T | null | false | undefined,
  timeoutMs: number,
  label: string,
): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const value = pred();
    if (value) return value;
    await sleep(250);
  }
  throw new Error(`timeout waiting for ${label}`);
}

type TableParent = {
  isNullObject?: boolean;
  load: (s: string | string[]) => void;
  rowCount?: number;
};

async function snapshotSelectionParagraph(): Promise<Record<string, unknown>> {
  if (typeof Word === 'undefined') {
    return { office: false };
  }
  return Word.run(async (context) => {
    const tables = context.document.body.tables;
    tables.load('items');
    const para = context.document.getSelection().paragraphs.getFirst();
    para.load('text');
    const parentTable = (para as unknown as { parentTableOrNullObject?: TableParent }).parentTableOrNullObject;
    if (parentTable) parentTable.load('isNullObject, rowCount');
    let trackedChangeCount: number | null = null;
    try {
      const trackedChanges = (para as unknown as { getTrackedChanges?: () => { load: (s: string) => void; items?: unknown[] } }).getTrackedChanges?.();
      if (trackedChanges) {
        trackedChanges.load('items');
      }
      await context.sync();
      trackedChangeCount = trackedChanges ? trackedChanges.items?.length ?? 0 : null;
    } catch {
      await context.sync();
    }
    const inTable = parentTable ? parentTable.isNullObject === false : false;
    const text = foldWs(para.text || '');
    return {
      office: true,
      tableCount: tables.items.length,
      hit: text.length > 0,
      inTable,
      tableRowCount: inTable ? parentTable?.rowCount ?? null : null,
      trackedChangeCount,
      textLen: text.length,
      textHead: text.slice(0, 80),
      textTail: text.slice(-80),
      contains2500: text.includes('2500'),
      containsRegistrationFees: text.includes('הוצאות הרישום'),
    };
  });
}

async function snapshotCardParagraph(card: HTMLElement): Promise<Record<string, unknown>> {
  const header = card.querySelector<HTMLElement>('.cc-clause-header');
  header?.click();
  // Content search can run several Word.run attempts (full + tail) before select() settles.
  await sleep(4000);
  return snapshotSelectionParagraph();
}

function liveCard(clauseNumber: string): HTMLElement {
  return requireCard(clauseNumber);
}

function enabledUndoButton(clauseNumber: string): HTMLButtonElement | null {
  const card = findCard(clauseNumber);
  if (!card) return null;
  const undo = findUndoButton(card);
  if (!undo || undo.disabled) return null;
  return undo;
}

function suggestionReady(card: HTMLElement): boolean {
  if (card.getAttribute('data-ai-streaming') === 'true') return false;
  const submit = findSubmitButton(card);
  return previewLooksLikeAiSuggestion(previewColorStats(card)) && Boolean(submit && !submit.disabled);
}

function previewPlainText(card: HTMLElement): string {
  const root = card.querySelector('.cc-selected-text-xml') || card;
  return foldQrCheckText(root.textContent || '');
}

function previewInsTexts(card: HTMLElement): string[] {
  const root = card.querySelector('.cc-selected-text-xml') || card;
  return Array.from(root.querySelectorAll('ins')).map((el) => foldQrCheckText(el.textContent || ''));
}

function previewColorStats(card: HTMLElement): Record<string, unknown> {
  const root = card.querySelector('.cc-selected-text-xml') || card;
  const ins = Array.from(root.querySelectorAll('ins'));
  const del = Array.from(root.querySelectorAll('del'));
  const isNew = (el: Element) => el.classList.contains('track-change-new');
  return {
    insTotal: ins.length,
    insBlue: ins.filter(isNew).length,
    insRed: ins.filter((el) => !isNew(el)).length,
    delTotal: del.length,
    delBlue: del.filter(isNew).length,
    delRed: del.filter((el) => !isNew(el)).length,
    htmlHasTrackChangeNew: (root.innerHTML || '').includes('track-change-new'),
    previewHtmlLen: (root.innerHTML || '').length,
  };
}

async function runSnapshot(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = liveCard(clauseNumber);
  card.scrollIntoView({ block: 'center' });
  await sleep(200);
  const afterScroll = liveCard(clauseNumber);
  const snap = await snapshotCardParagraph(afterScroll);
  const fresh = liveCard(clauseNumber);
  const actions = cardActionSignals(fresh);
  const aiBtn = fresh.querySelector<HTMLButtonElement>('[data-testid="cc-ai-btn"]');
  return {
    clauseNumber,
    clauseId: fresh.getAttribute('data-clause-id'),
    aiEnabled: Boolean(aiBtn && !aiBtn.disabled),
    preview: previewColorStats(fresh),
    submitted: cardLooksSubmitted(fresh),
    streaming: fresh.getAttribute('data-ai-streaming') === 'true',
    hasPreview: Boolean(
      fresh.querySelector('ins.track-change-new, ins.track-change-insertion, del.track-change-del, del.track-change-deletion'),
    ),
    hasSubmit: Boolean(actions.hasSubmit),
    hasUndo: Boolean(actions.hasUndo),
    actions,
    ...snap,
  };
}

async function runQuickResponse(args: {
  clauseNumber?: string;
  type?: string;
}): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const optionId = String(args.type || 'see_proposed_correction');
  const card = requireCard(clauseNumber);
  if (cardLooksSubmitted(card)) {
    throw new Error('quick-response refused: card already submitted — undo-response first');
  }
  card.scrollIntoView({ block: 'center' });
  await sleep(800);

  // Leftover QR/AI preview makes baselineText already contain the suffix, so a
  // second click is a no-op and submit never grows the live paragraph. Always
  // clear when possible so the paint wait sees a real delta.
  const clearBtn = liveCard(clauseNumber).querySelector<HTMLButtonElement>('[data-testid="cc-clear-ai"]');
  if (clearBtn && !clearBtn.disabled) {
    clearBtn.click();
    await sleep(500);
  }

  const baselinePreview = previewColorStats(liveCard(clauseNumber));
  const previewBaselineText = previewPlainText(liveCard(clauseNumber));
  // Short clauses (party lines / headings) often show a longer TC/CR string in
  // the card while Word live text is the short paragraph. Apply checks must use
  // the live Word baseline or they false-fail with apply_lost_baseline_head.
  let baselineText = previewBaselineText;
  try {
    const liveSnap = await snapshotCardParagraph(liveCard(clauseNumber));
    const liveLen = Number(liveSnap.textLen) || 0;
    const liveHead = String(liveSnap.textHead || '');
    const liveTail = String(liveSnap.textTail || '');
    if (
      liveLen > 0 &&
      liveHead &&
      liveTail &&
      liveHead === liveTail &&
      liveLen === liveHead.length
    ) {
      baselineText = liveHead;
    }
  } catch {
    // keep preview baseline
  }
  const qrBtn = findQuickResponseButton(liveCard(clauseNumber));
  if (!qrBtn) throw new Error('quick-response button missing');
  const live = liveCard(clauseNumber);
  if (qrBtn.disabled || isUnusableQrCard(live)) {
    return {
      ok: true,
      skipped: true,
      reason: qrBtn.disabled
        ? 'quick-response disabled (no client request)'
        : 'no client request / clause not found',
      clauseNumber,
      clauseId: live.getAttribute('data-clause-id'),
      baselineText: baselineText.slice(0, 120),
    };
  }

  let clickedOption = false;
  for (let attempt = 1; attempt <= 2; attempt++) {
    findQuickResponseButton(liveCard(clauseNumber))?.click();
    try {
      const option = await waitFor(
        () => findQuickResponseOption(document, optionId),
        2500,
        `quick-response option ${optionId}`,
      );
      option.click();
      clickedOption = true;
      break;
    } catch {
      // Toggle / scroll may have closed the portaled menu.
    }
  }
  if (!clickedOption) {
    throw new Error(`quick-response menu did not stay open for ${optionId}`);
  }

  await waitFor(() => {
    const current = findCard(clauseNumber);
    if (!current) return null;
    const stats = previewColorStats(current);
    const text = previewPlainText(current);
    const changed =
      stats.htmlHasTrackChangeNew ||
      Number(stats.insBlue) > Number(baselinePreview.insBlue) ||
      text.includes('ראה תיקון מוצע');
    return changed ? current : null;
  }, 30000, 'quick-response preview painted');

  const ready = liveCard(clauseNumber);
  const preview = previewColorStats(ready);
  const previewText = previewPlainText(ready);
  const insTexts = previewInsTexts(ready);
  const checks = evaluateSeeProposedPreview({
    previewText,
    baselineText,
    insBlue: Number(preview.insBlue) || 0,
    htmlHasTrackChangeNew: preview.htmlHasTrackChangeNew === true,
    insTexts,
  });
  log(`${PREFIX} quick-response`, {
    clauseNumber,
    optionId,
    checks,
    previewText: previewText.slice(-80),
  });
  return {
    ok: true,
    skipped: false,
    clauseNumber,
    clauseId: ready.getAttribute('data-clause-id'),
    optionId,
    clickedOption,
    baselineText,
    previewText,
    preview,
    insTexts,
    checks,
    previewOk: checks.ok,
  };
}

async function runClear(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = requireCard(clauseNumber);
  card.scrollIntoView({ block: 'center' });
  await sleep(200);
  const clearBtn = card.querySelector<HTMLButtonElement>('[data-testid="cc-clear-ai"]');
  if (!clearBtn) {
    return { ok: true, clauseNumber, cleared: false, reason: 'clear button not present' };
  }
  clearBtn.click();
  await sleep(300);
  const preview = previewColorStats(card);
  log(`${PREFIX} cleared`, { clauseNumber, clauseId: card.getAttribute('data-clause-id'), preview });
  return { ok: true, clauseNumber, cleared: true, clauseId: card.getAttribute('data-clause-id'), preview };
}

function cardIsStreaming(clauseNumber: string): boolean {
  return findCard(clauseNumber)?.getAttribute('data-ai-streaming') === 'true';
}

/**
 * AI on a card with both BOC and past-negotiation sources only opens a portaled
 * menu. scrollIntoView can still emit scroll after that click, and
 * useFixedDropdownPosition closes the menu on scroll — so a fixed 400ms wait
 * often finds no BOC button and never starts generation.
 */
async function clickAiAndStartGeneration(clauseNumber: string): Promise<{
  clickedBoc: boolean;
  sawMenu: boolean;
  attempts: number;
}> {
  let sawMenu = false;
  let clickedBoc = false;
  for (let attempt = 1; attempt <= 2; attempt++) {
    const card = liveCard(clauseNumber);
    const aiBtn = card.querySelector<HTMLButtonElement>('[data-testid="cc-ai-btn"]');
    if (!aiBtn || aiBtn.disabled) {
      throw new Error(`AI button missing or disabled (attempt ${attempt})`);
    }
    aiBtn.click();
    try {
      const boc = await waitFor(() => {
        if (cardIsStreaming(clauseNumber)) return 'streaming' as const;
        const btn = findAiSourceBocButton(document);
        return btn && !btn.disabled ? btn : null;
      }, 2000, 'AI source menu or streaming');
      if (boc === 'streaming') {
        return { clickedBoc, sawMenu, attempts: attempt };
      }
      sawMenu = true;
      boc.click();
      clickedBoc = true;
      return { clickedBoc, sawMenu, attempts: attempt };
    } catch {
      // First click may have closed an already-open menu (toggle). Retry once.
    }
  }
  throw new Error(
    'AI click did not open the source menu and did not start streaming. The menu may have closed on scroll.',
  );
}

async function runAi(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = requireCard(clauseNumber);
  card.scrollIntoView({ block: 'center' });
  // Let the clause list finish scrolling before opening the AI menu — a late
  // scroll event closes the portaled source menu without starting generation.
  await sleep(800);

  const baselinePreview = previewColorStats(liveCard(clauseNumber));
  const start = await clickAiAndStartGeneration(clauseNumber);
  log(`${PREFIX} ai clicked`, { clauseNumber, ...start });

  // Existing CR redlines make suggestionReady() a false positive. Require a real stream.
  await waitFor(
    () => (cardIsStreaming(clauseNumber) ? true : null),
    20000,
    'AI streaming start',
  );

  await waitFor(() => {
    const current = findCard(clauseNumber);
    if (!current || current.getAttribute('data-ai-streaming') === 'true') return null;
    const clear = current.querySelector('[data-testid="cc-clear-ai"]');
    const stats = previewColorStats(current);
    const changed =
      stats.htmlHasTrackChangeNew ||
      stats.previewHtmlLen !== baselinePreview.previewHtmlLen ||
      stats.insBlue !== baselinePreview.insBlue ||
      stats.delTotal !== baselinePreview.delTotal;
    return clear && changed ? current : null;
  }, 90000, 'AI suggestion finished (stream ended + preview changed)');
  const readyCard = liveCard(clauseNumber);
  const previewAtReady = previewColorStats(readyCard);
  log(`${PREFIX} ai suggestionReady (pre-hybrid-wait)`, {
    clauseNumber,
    sawStreaming: true,
    ...start,
    baselinePreview,
    previewAtReady,
  });
  // Hybrid preview is debounced 350ms + a Word.run; the blue swap happens after this.
  await sleep(3500);
  const previewAfterHybrid = previewColorStats(liveCard(clauseNumber));
  log(`${PREFIX} ai after hybrid window`, { clauseNumber, previewAtReady, previewAfterHybrid });
  return {
    ok: true,
    clauseNumber,
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    suggestionReady: true,
    sawStreaming: true,
    clickedBoc: start.clickedBoc,
    sawMenu: start.sawMenu,
    baselinePreview,
    previewAtReady,
    previewAfterHybrid,
  };
}

function submitHandlerStarted(clauseNumber: string): boolean {
  const card = findCard(clauseNumber);
  if (!card) return false;
  if (card.getAttribute('data-response-submitting') === 'true') return true;
  if (card.getAttribute('data-response-submitted') === 'true') return true;
  const submit = findSubmitButton(card);
  const label = foldLabel(submit?.textContent || '');
  if (isSubmitToResponseLabel(label) && (label.includes('מחיל') || label.includes('applying'))) {
    return true;
  }
  if (findUndoButton(card) && !findUndoButton(card)!.disabled) return true;
  if (label.includes('לא ניתן') || label.includes('could not apply')) return true;
  return false;
}

/**
 * Click שלח לתגובה on the live card. Do not snapshot via the clause header
 * first — that click is go-to, steals 4s, and looks like the agent never
 * pressed the submit button.
 */
async function clickSubmitToResponse(clauseNumber: string): Promise<Record<string, unknown>> {
  const card = liveCard(clauseNumber);
  const submit = card.querySelector<HTMLButtonElement>('[data-testid="cc-submit-to-response"]')
    || findSubmitButton(card);
  if (!submit) {
    throw new Error(`submit button missing ${JSON.stringify(cardActionSignals(card))}`);
  }
  if (submit.disabled) {
    throw new Error(`submit button disabled ${JSON.stringify(cardActionSignals(card))}`);
  }
  const label = (submit.textContent || '').replace(/\s+/g, ' ').trim();
  if (!isSubmitToResponseLabel(label)) {
    throw new Error(`refusing to click non-submit button label=${JSON.stringify(label)}`);
  }
  submit.scrollIntoView({ block: 'center', inline: 'nearest' });
  await sleep(200);
  const live = liveCard(clauseNumber).querySelector<HTMLButtonElement>('[data-testid="cc-submit-to-response"]')
    || findSubmitButton(liveCard(clauseNumber));
  if (!live || live.disabled) {
    throw new Error('submit button disappeared or disabled after scroll');
  }
  live.click();
  const clickInfo = {
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    testid: live.getAttribute('data-testid'),
    label,
    preview: previewColorStats(liveCard(clauseNumber)),
  };
  log(`${PREFIX} submit clicked`, clickInfo);
  return clickInfo;
}

async function runSubmit(args: { clauseNumber?: string; baselineText?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  const card = requireCard(clauseNumber);
  if (!suggestionReady(card)) {
    throw new Error('submit refused: AI suggestion is not ready yet');
  }

  const clickInfo = await clickSubmitToResponse(clauseNumber);
  try {
    await waitFor(
      () => (submitHandlerStarted(clauseNumber) ? true : null),
      4000,
      'submit handler started (מחיל / data-response-submitting)',
    );
  } catch (err) {
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify({
        ...clickInfo,
        actions: cardActionSignals(liveCard(clauseNumber)),
      })}`,
    );
  }

  try {
    await waitFor(
      () => enabledUndoButton(clauseNumber),
      90000,
      'undo button after apply finished',
    );
  } catch (err) {
    const fresh = liveCard(clauseNumber);
    const lastStage =
      typeof window !== 'undefined' ? (window as any).__mccLastQrStage : undefined;
    const lastStages =
      typeof window !== 'undefined' ? (window as any).__mccLastQrStages : undefined;
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify({
        clickLanded: true,
        actions: cardActionSignals(fresh),
        preview: previewColorStats(fresh),
        submitting: fresh.getAttribute('data-response-submitting'),
        lastQrStage: lastStage ?? null,
        lastQrStagesTail: Array.isArray(lastStages) ? lastStages.slice(-8) : null,
      })}`,
    );
  }

  const afterApply = await snapshotSelectionParagraph();
  const actions = cardActionSignals(liveCard(clauseNumber));
  const baselineText = String(args.baselineText || '');
  const applyChecks = baselineText
    ? evaluateSeeProposedApplyFromSnapshot({
        textHead: String(afterApply.textHead || ''),
        textTail: String(afterApply.textTail || ''),
        textLen: Number(afterApply.textLen) || 0,
        baselineText,
        hasUndo: Boolean(actions.hasUndo),
        tableOk: afterApply.tableCount == null ? undefined : true,
      })
    : undefined;
  const result = {
    ok: true,
    clauseNumber,
    clauseId: liveCard(clauseNumber).getAttribute('data-clause-id'),
    clickLanded: true,
    clickInfo,
    afterApply,
    actions,
    applyChecks,
    applyOk: applyChecks ? applyChecks.ok : undefined,
  };
  log(`${PREFIX} submit`, result);
  return result;
}

async function runUndoResponse(args: { clauseNumber?: string }): Promise<Record<string, unknown>> {
  const clauseNumber = String(args.clauseNumber || '7.9');
  let undo: HTMLButtonElement;
  try {
    undo = await waitFor(() => {
      const current = findCard(clauseNumber);
      if (!current) return null;
      const btn = findUndoButton(current);
      if (btn && !btn.disabled) return btn;
      return null;
    }, 15000, 'enabled undo button');
  } catch (err) {
    const current = findCard(clauseNumber);
    const signals = current ? cardActionSignals(current) : { cardFound: false };
    throw new Error(
      `${err instanceof Error ? err.message : String(err)} ${JSON.stringify(signals)}`,
    );
  }

  const beforeUndo = await snapshotCardParagraph(liveCard(clauseNumber));
  const liveUndo = enabledUndoButton(clauseNumber) || undo;
  liveUndo.click();

  await waitFor(
    () => {
      const current = findCard(clauseNumber);
      if (!current) return null;
      if (cardLooksSubmitted(current)) return null;
      return findSubmitButton(current);
    },
    45000,
    'submit button after undo finished',
  );
  await sleep(600);

  const afterCard = liveCard(clauseNumber);
  const afterUndo = await snapshotCardParagraph(afterCard);
  const tableOk = beforeUndo.tableCount === afterUndo.tableCount;
  const rowsOk =
    beforeUndo.tableRowCount == null || beforeUndo.tableRowCount === afterUndo.tableRowCount;
  const beforeBlob = `${beforeUndo.textHead || ''} ${beforeUndo.textTail || ''}`;
  const afterBlob = `${afterUndo.textHead || ''} ${afterUndo.textTail || ''}`;
  const hadSuffix = /ראה תיקון מוצע/.test(beforeBlob);
  const stillHasSuffix = /ראה תיקון מוצע/.test(afterBlob);
  const textChanged =
    Number(beforeUndo.textLen) !== Number(afterUndo.textLen) || (hadSuffix && !stillHasSuffix);
  // Apply sometimes hit a wrong short cell; the card paragraph was already clean.
  // Undo still clears submitted state — treat that as success for the sweep.
  const alreadyClean = !hadSuffix && !stillHasSuffix;
  const result = {
    ok: Boolean(tableOk && rowsOk && (textChanged || alreadyClean)),
    clauseNumber,
    clauseId: afterCard.getAttribute('data-clause-id'),
    checks: { tableOk, rowsOk, textChanged, alreadyClean, hadSuffix, stillHasSuffix },
    beforeUndo,
    afterUndo,
  };
  log(`${PREFIX} undo-response`, result);
  return result;
}

async function runScanCards(args: { needle?: string }): Promise<Record<string, unknown>> {
  const needle = String(args.needle || '').trim();
  const needleFold = foldWs(needle);
  const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
  const hits: Array<{ clauseId: string; clauseNumber: string; head: string }> = [];
  for (const card of cards) {
    const text = foldWs(card.innerText || '');
    if (needleFold && text.includes(needleFold)) {
      hits.push({
        clauseId: card.getAttribute('data-clause-id') || '',
        clauseNumber: card.getAttribute('data-clause-number') || '',
        head: text.slice(0, 200),
      });
    }
  }
  return { needle, hitCount: hits.length, hits };
}

async function runSearch(args: { needle?: string }): Promise<Record<string, unknown>> {
  const needle = String(args.needle || '2500').slice(0, 80);
  if (typeof Word === 'undefined') return { office: false };
  return Word.run(async (context) => {
    const hits = context.document.body.search(needle, { matchCase: false });
    hits.load('items');
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load('items');
    await context.sync();
    const found = [];
    for (let i = 0; i < Math.min(hits.items.length, 8); i++) {
      const para = hits.items[i].paragraphs.getFirst();
      para.load('text');
      found.push(para);
    }
    for (let i = 0; i < paragraphs.items.length; i++) {
      paragraphs.items[i].load('text');
    }
    await context.sync();
    const needleFold = foldWs(needle);
    const scanned: Array<{ index: number; textLen: number; textHead: string; textTail: string }> = [];
    for (let i = 0; i < paragraphs.items.length; i++) {
      const text = foldWs(paragraphs.items[i].text || '');
      if (needleFold && text.includes(needleFold)) {
        scanned.push({
          index: i,
          textLen: text.length,
          textHead: text.slice(0, 100),
          textTail: text.slice(-80),
        });
      }
    }
    return {
      office: true,
      needle,
      hitCount: hits.items.length,
      scanHitCount: scanned.length,
      paragraphs: found.map((p) => {
        const text = foldWs(p.text || '');
        return { textLen: text.length, textHead: text.slice(0, 100), textTail: text.slice(-80) };
      }),
      scanned: scanned.slice(0, 8),
    };
  });
}

async function handleCommand(cmd: { id: string; action: string; args?: Record<string, unknown> }): Promise<unknown> {
  const clauseNumber = String(cmd.args?.clauseNumber || '7.9');
  if (cmd.action === 'ping') {
    const text = (document.body.innerText || '').replace(/\s+/g, ' ').trim();
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
    return {
      agentBridge: 'accepted-ooxml-v1',
      applyVerify: 'accepted-ooxml-v1',
      cards: cards.length,
      cardIds: cards.map((c) => c.getAttribute('data-clause-id')),
      cardNumbers: cards.map((c) => c.getAttribute('data-clause-number')),
      match79: !!findCard('7.9'),
      match79id: findCard('7.9')?.getAttribute('data-clause-id') || null,
      title: document.title,
      heading: document.querySelector('h1, h2, h3')?.textContent?.trim() || '',
      bodyHead: text.slice(0, 400),
    };
  }
  if (cmd.action === 'snapshot') return runSnapshot({ clauseNumber });
  if (cmd.action === 'search') return runSearch({ needle: String(cmd.args?.needle || '2500') });
  if (cmd.action === 'scan-cards') return runScanCards({ needle: String(cmd.args?.needle || '') });
  if (cmd.action === 'restore-baseline') {
    throw new Error('restore-baseline is disabled: never rewrite the cell. Use Word Undo until exhausted.');
  }
  if (cmd.action === 'clear') return runClear({ clauseNumber });
  if (cmd.action === 'quick-response') {
    return runQuickResponse({
      clauseNumber,
      type: String(cmd.args?.type || 'see_proposed_correction'),
    });
  }
  if (cmd.action === 'ai') return runAi({ clauseNumber });
  if (cmd.action === 'submit') {
    return runSubmit({
      clauseNumber,
      baselineText: String(cmd.args?.baselineText || ''),
    });
  }
  if (cmd.action === 'undo-response') return runUndoResponse({ clauseNumber });
  throw new Error(`unknown action: ${cmd.action}`);
}

type AgentWindow = Window & {
  __courtyardMccAgentStop?: () => void;
  __courtyardMccAgentHandleCommand?: typeof handleCommand;
};

export function installMccAgentBridge(): void {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV === 'production') return;

  const w = window as unknown as AgentWindow;
  w.__courtyardMccAgentHandleCommand = handleCommand;
  if (w.__courtyardMccAgentStop) {
    w.__courtyardMccAgentStop();
    w.__courtyardMccAgentStop = undefined;
  }

  let stopped = false;
  const loop = async () => {
    while (!stopped) {
      try {
        const response = await fetch('/__agent__/poll?v=accepted-ooxml-v1', { cache: 'no-store' });
        if (response.status === 204) continue;
        if (!response.ok) {
          await sleep(2000);
          continue;
        }
        const cmd = (await response.json()) as { id: string; action: string; args?: Record<string, unknown> };
        log(`${PREFIX} got command`, { id: cmd.id, action: cmd.action });
        const handler = w.__courtyardMccAgentHandleCommand || handleCommand;
        try {
          const result = await handler(cmd);
          await fetch('/__agent__/result', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: cmd.id, ok: true, result }),
          });
        } catch (err) {
          await fetch('/__agent__/result', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: cmd.id,
              ok: false,
              error: err instanceof Error ? err.message : String(err),
            }),
          });
        }
      } catch {
        await sleep(2000);
      }
    }
  };
  void loop();
  w.__courtyardMccAgentStop = () => {
    stopped = true;
  };
}

if (typeof window !== 'undefined') {
  (window as unknown as AgentWindow).__courtyardMccAgentHandleCommand = handleCommand;
}
